import {StateChanges} from './stateChanges'
import {RunStates} from './runStates'
import savedExperiments from './savedExperimentUtils'
import * as firebase from 'firebase'
import {firebaseStore} from './firebaseModule'
import defaultSettings from './defaultSettings'
import {resetAfterFeatureChanges, resetResultsAndMarkUnsaved, resetTestResultsAndMarkUnsaved} from './resetState'
import treeService from '../texts/treeService'

function gtag () { window.dataLayer.push(arguments) }

export function addToSaveList (selection, state, listName, limit) {
  const currentList = state[listName]
  // only one item for a given title allowed
  const updatedList = currentList.filter(item => item.name !== selection.name)
  updatedList.unshift(selection)
  if (limit && updatedList.length > limit) {
    updatedList.pop()
  }
  firebaseStore(listName, updatedList)
}

export function removeFromSaveList (selection, state, listName) {
  const currentList = state[listName]
  const updatedList = currentList.filter(item => item.name !== selection.name)
  firebaseStore(listName, updatedList)
}

function createSaveObject (classData, context) {
  return {
    name: classData.displayName,
    date: Date.now(),
    text: savedExperiments.textToSave(classData),
    context: context
  }
}

export default {
  [StateChanges.ADD_CLASS]: function (state, classData) {
    resetResultsAndMarkUnsaved(state)
    state.classes.push(classData)
    state.experimentSaved = false
    if (state.isLoggedIn && classData.hasKeys()) {
      addToSaveList(createSaveObject(classData, 'Class'), state, 'recent', 10)
    }
  },
  [StateChanges.REMOVE_CLASS]: function (state, classData) {
    resetResultsAndMarkUnsaved(state)
    const classIndex = state.classes.indexOf(classData)
    if (classIndex > -1) {
      state.classes.splice(classIndex, 1)
      state.experimentSaved = false
    }
  },
  [StateChanges.UPDATE_CLASS]: function (state, {oldClassData, newClassData}) {
    resetResultsAndMarkUnsaved(state)
    const classIndex = state.classes.indexOf(oldClassData)
    if (classIndex > -1) {
      // Vue cannot detect a normal array set
      state.classes.splice(classIndex, 1, newClassData)
      state.experimentSaved = false
      if (state.isLoggedIn && newClassData.hasKeys()) {
        addToSaveList(createSaveObject(newClassData, 'Class'), state, 'recent', 10)
      }
    }
  },
  [StateChanges.SET_TEST_TEXT]: function (state, testText) {
    resetTestResultsAndMarkUnsaved(state)
    state.testText = testText
    state.experimentSaved = false
    if (state.isLoggedIn && testText.hasKeys()) {
      addToSaveList(createSaveObject(testText, 'Text'), state, 'recent', 10)
    }
  },
  [StateChanges.SET_CHUNKING] (state, chunking) {
    resetTestResultsAndMarkUnsaved(state)
    // right now, chunking is just a string, but it may need to be changed to a more complex object if users can select custom chunking
    state.chunking = chunking
  },
  [StateChanges.SET_FEATURE_SETS] (state, featureSettings) {
    resetAfterFeatureChanges(state)
    state.featureSettings = featureSettings
  },
  [StateChanges.SET_FEATURE_RESULTS]: function (state, features) {
    state.features = features
    state.featuresState = RunStates.COMPLETE
  },
  [StateChanges.SET_CROSSVALIDATION_RESULTS]: function (state, cvResults) {
    state.crossValidationResults = cvResults
    state.cvState = RunStates.COMPLETE
  },
  [StateChanges.SET_TEST_TEXT_RESULTS]: function (state, testResults) {
    state.testTextResults = treeService.treeSort(testResults, chunkData => chunkData.textKey)
    state.classifyState = RunStates.COMPLETE
  },
  [StateChanges.RESET_EXPERIMENT]: function (state, optionalArgs) {
    // console.log(optionalArgs)
    if (optionalArgs && (optionalArgs['preparing_to_load'])) {
      // no gtag for reset when it's just preparing to load
    } else {
      gtag('config', 'UA-103843509-5', {'page_path': 'new-experiment'})
    }
    resetResultsAndMarkUnsaved(state)
    state.classes = []
    state.testText = null
    state.featureSettings = defaultSettings.getDefaultFeatureSettings()
    state.removedFeatures = defaultSettings.getDefaultRemovedFeatures()
    state.chunking = defaultSettings.getDefaultChunking()
    state.experimentName = null
  },
  [StateChanges.SET_EXPERIMENT_NAME] (state, name) {
    state.experimentName = name
  },
  [StateChanges.EXTRACT_FEATURES_RUNNING] (state) {
    state.featuresState = RunStates.RUNNING
  },
  [StateChanges.UPDATE_FEATURES_RUNNING] (state) {
    state.updateFeaturesState = RunStates.RUNNING
  },
  [StateChanges.CROSSVALIDATION_RUNNING] (state) {
    state.cvState = RunStates.RUNNING
  },
  [StateChanges.CLASSIFY_RUNNING] (state) {
    state.classifyState = RunStates.RUNNING
  },
  [StateChanges.EXTRACT_FEATURES_TIMED_OUT]: function (state) {
    state.featuresState = RunStates.TIMED_OUT
  },
  [StateChanges.EXTRACT_FEATURES_FAILED]: function (state) {
    state.featuresState = RunStates.FAILED
  },
  [StateChanges.CROSSVALIDATION_TIMED_OUT]: function (state) {
    state.cvState = RunStates.TIMED_OUT
  },
  [StateChanges.CROSSVALIDATION_FAILED]: function (state) {
    state.cvState = RunStates.FAILED
  },
  [StateChanges.CLASSIFY_TIMED_OUT]: function (state) {
    state.classifyState = RunStates.TIMED_OUT
  },
  [StateChanges.CLASSIFY_FAILED]: function (state) {
    state.classifyState = RunStates.FAILED
  },
  [StateChanges.SET_TEXT_NAME]: function (state, {text, name}) {
    text.setNameInternal(name)
  },
  [StateChanges.LOGIN_REQUESTED] (state) {
    state.loginInProgress = true
    window.localStorage.setItem('savedAppState', JSON.stringify(savedExperiments.getCurrentExperimentData(state)))
  },
  [StateChanges.LOGIN_COMPLETE] (state) {
    state.loginInProgress = false
    state.isLoggedIn = true
    state.username = firebase.auth().currentUser.displayName
  },
  [StateChanges.LOGIN_CANCELLED] (state) {
    state.loginInProgress = false
    window.localStorage.removeItem('savedAppState')
  },
  [StateChanges.LOGIN_CONTINUING] (state) {
    state.loginInProgress = true
  },
  [StateChanges.LOGGED_OUT] (state) {
    state.isLoggedIn = false
    state.username = null
    state.favorites = []
    state.recent = []
    state.savedExperiments = []
  },
  [StateChanges.FIREBASE_RECEIVED_UPDATE] (state, {listName, value}) {
    state[listName] = value
  },
  [StateChanges.SAVE_FAVORITE] (state, { favorite, context }) {
    if (favorite.hasKeys()) {
      addToSaveList(createSaveObject(favorite, context), state, 'favorites')
    }
  },
  [StateChanges.SAVE_EXPERIMENT] (state) {
    addToSaveList({
      name: state.experimentName,
      date: Date.now(),
      experimentData: savedExperiments.getCurrentExperimentData(state)
    }, state, 'savedExperiments')
    gtag('config', 'UA-103843509-5', {'page_path': 'save-experiment'})
    state.experimentSaved = true
  },
  [StateChanges.UNSAVE_EXPERIMENT] (state, savedExperiment) {
    removeFromSaveList({
      name: savedExperiment.name
    }, state, 'savedExperiments')
  }
}



// WEBPACK FOOTER //
// ./src/store/mutations.js